// Subtle connection cues for the parent (viewer): a soft, descending two-note
// tone when the link to the monitor drops, and a gentle rising three-note tone
// when it comes back. Deliberately quiet and short so a night-time monitor
// nudges the parent without ever feeling like an alarm.
//
// Tones are synthesised with the Web Audio API — no audio files to ship, and it
// works offline. A single AudioContext is created lazily and reused. Mobile
// browsers start a context 'suspended' until a user gesture, so we unlock it on
// the first interaction and also resume() right before playing.

let ctx = null;
let unlockBound = false;

function getContext() {
  if (ctx) return ctx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  try {
    ctx = new Ctx();
  } catch (e) {
    return null;
  }
  bindUnlock();
  return ctx;
}

// A context is 'suspended' until a user gesture. Create (if needed) and resume
// it on the first interaction so a later cue isn't silently dropped. Creating it
// inside the gesture means it starts in the 'running' state on iOS rather than
// stuck suspended.
function bindUnlock() {
  if (unlockBound) return;
  unlockBound = true;
  const unlock = () => {
    const c = getContext();
    if (c && c.state === 'suspended') c.resume().catch(() => {});
  };
  ['pointerdown', 'touchend', 'click', 'keydown'].forEach((evt) =>
    window.addEventListener(evt, unlock, { passive: true })
  );
}

// Prime the audio path early (viewer only): bind the gesture listeners now so
// the parent's first tap creates and resumes the context, well before any drop.
// Without this the very first "lost" cue can arrive while the context is still
// suspended (no prior gesture bound to it) and be silently dropped on iOS.
export function initCue() {
  bindUnlock();
}

// Play a short sequence of soft notes. Each note gets a gentle attack/release
// envelope (no clicks) and a low peak gain so the whole thing stays subtle.
// `type` picks the oscillator waveform: 'sine' for the calm cues, or a brighter
// waveform (e.g. 'square') for the outage alarm so it cuts through across a room.
function playSequence(notes, peak, type = 'sine') {
  const c = getContext();
  if (!c) return;
  if (c.state === 'suspended') c.resume().catch(() => {});

  let t = c.currentTime + 0.02;
  notes.forEach(({ freq, dur }) => {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);

    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(peak, t + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);

    osc.connect(gain);
    gain.connect(c.destination);
    osc.start(t);
    osc.stop(t + dur + 0.02);
    t += dur + 0.03; // small gap between notes keeps it calm, not a trill
  });
}

// Descending, calm — "the link went down".
export function playConnectionLost() {
  playSequence(
    [
      { freq: 523.25, dur: 0.2 }, // C5
      { freq: 392.0, dur: 0.32 }, // G4
    ],
    0.11
  );
}

// Rising, reassuring — "we're back".
export function playConnectionRestored() {
  playSequence(
    [
      { freq: 392.0, dur: 0.14 }, // G4
      { freq: 523.25, dur: 0.14 }, // C5
      { freq: 659.25, dur: 0.26 }, // E5
    ],
    0.1
  );
}

// Gentle low-high-low chime — "the monitor's battery is running low". A touch
// more insistent than the connection cues (a repeated dip that draws the ear)
// but still soft, since it repeats every few minutes through the night.
export function playBatteryWarning() {
  playSequence(
    [
      { freq: 440.0, dur: 0.16 }, // A4
      { freq: 587.33, dur: 0.16 }, // D5
      { freq: 440.0, dur: 0.28 }, // A4
    ],
    0.12
  );
}

// Urgent, siren-like alert for a *sustained* monitor outage — repeated by the
// parent until the link recovers. A prolonged drop means no eyes on the baby, so
// this is deliberately loud and attention-grabbing: a bright square waveform
// (rich in harmonics, so it cuts through a noisy room far better than the soft
// sine cues) driving a rapid two-tone triple pulse at a high peak gain. Still a
// synthesised tone, but now unmistakably an alarm rather than a gentle nudge.
export function playDisconnectedAlarm() {
  playSequence(
    [
      { freq: 988.0, dur: 0.18 }, // B5
      { freq: 784.0, dur: 0.18 }, // G5
      { freq: 988.0, dur: 0.18 }, // B5
      { freq: 784.0, dur: 0.28 }, // G5 — longer final pulse
    ],
    0.3,
    'square'
  );
}
